import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import {
  faArrowLeft,
  faArrowRight,
  faCheck,
  faPencil,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { motion, useDragControls, useMotionValue } from "motion/react";
import { useQueryClient } from "@tanstack/react-query";
import { isSameMonth } from "date-fns";

import Icon from "common/components/Icon";
import { toCount, toPhone } from "common/utils/formatting";
import Image from "common/components/Image";
import {
  addDays,
  fromISODateTime,
  fromISODateTimeInTimezone,
  getTimezone,
  hasSameTimezoneOffset,
  isSameDate,
  isSameDateTime,
  isValidDate,
  toClinicDate,
  toISODate,
  toLocaleDate,
  toTime,
} from "common/utils/dates";
import { useMatch, useNavigate } from "common/router/hooks";
import { equals } from "common/utils/sets";
import {
  AppointmentStatus,
  CalendarResource,
  ResourceAvailabilityByResource,
  ResourceAvailabilityByTime,
  AppointmentKindSlug,
} from "common/types/scheduler";
import { PatientKind } from "common/types";
import { titleCased } from "common/utils/strings";
import ResourceImage from "common/components/scheduler/ResourceImage";
import { useDebounceCallback } from "common/hooks";
import { appointmentKindToExoticKind } from "common/constants/scheduler";
import { isDev } from "common/utils/debug";
import { LinkButtonRef } from "common/components/LinkButton";

import Button from "client/common/components/buttons/Button";
import { PatientForMultiSelectPanel } from "client/accounts/components/PatientMultiSelectPanel";
import { PatientMultiSelect } from "client/accounts/components/PatientSelect";
import { useClinic } from "client/clinics/hooks";
import { useDefaultCalendarId } from "client/scheduler/hooks/data";
import { Paths, urls } from "client/routes";
import Alert from "client/common/components/content/Alert";
import { IClient } from "client/accounts/types";
import PillButton from "client/common/components/buttons/PillButton";
import { useAccountCreatePaymentDrawer, useAccountUpdateDrawer } from "client/accounts/stackables";
import SelectButton from "client/common/components/buttons/SelectButton";
import { useUpdateClientMutation } from "client/accounts/hooks";
import PaymentMethod from "client/common/components/content/PaymentMethod";
import TextChip from "client/common/components/content/TextChip";
import {
  getPaymentOptionChargeabilityChip,
  isPaymentOptionChargeable,
} from "client/payments/utils";
import blankPayment from "client/payments/images/blank_payment.svg";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import { useClassifyApptMutation } from "client/turbo/hooks";
import { ClassifyAppointmentKindResult } from "client/turbo/types";
import { Clinic } from "client/clinics/types";

import { getAppointmentBookingInfo } from "../utils/calendar";
import { keys } from "../hooks/constants";
import {
  useAppointmentBulkUpdateMutation,
  useAppointmentBulkCreateMutation,
  useCalendarResourceAvailabilityByTimeQuery,
  useCalendarResourceAvailabilityByResourceQuery,
  useAppointmentBulkUpdateOptimisticMutation,
} from "../hooks";
import dogImage from "../images/dog_booking.svg";
import gradient from "../images/gradient.svg";
import AppointmentBookingPage from "./AppointmentBookingPage";
import { AppointmentBookingStep, CalendarInfo, ClientAppointment } from "../types";
import { useAppointmentBookingAlertDrawer } from "../stackables";
import AppointmentBookingAppointment from "./AppointmentBookingAppointment";

import styles from "../styles/AppointmentBookingSteps.module.less";

export type AppointmentBookingStepProps = {
  onPrevious: () => void;
  onNext: () => void;
  appointments: ClientAppointment[];
  info: CalendarInfo;
  client?: IClient;
};

const getNextAvailableLabel = (nextAvailableDate: Date): string => {
  const today = new Date();

  if (isSameDate(nextAvailableDate, today)) {
    return "Today";
  }
  if (isSameDate(nextAvailableDate, addDays(today, 1))) {
    return "Tomorrow";
  }

  // Will treat Saturday as the last day of the week like in a calendar
  const lastDayOfWeek = (() => {
    const lastDay = new Date(today);
    lastDay.setDate(today.getDate() + (6 - today.getDay()));
    return lastDay;
  })();

  if (nextAvailableDate <= lastDayOfWeek) {
    return "This week";
  }
  if (nextAvailableDate <= addDays(lastDayOfWeek, 7)) {
    return "Next week";
  }
  if (isSameMonth(nextAvailableDate, today)) {
    return "This month";
  }
  return toLocaleDate(nextAvailableDate, { month: "short", day: "numeric" });
};

const canProviderSeeSpecies = (resource: CalendarResource, species: PatientKind): boolean => {
  // If allowed_species is empty, provider can see all species
  if (resource.allowed_species.length === 0) {
    return true;
  }
  return resource.allowed_species.includes(species);
};

const AppointmentBookingStart = ({ onPrevious, onNext, info }: AppointmentBookingStepProps) => {
  const { search } = useMatch<Paths["scheduler"]["book"]>();

  const clinic = useClinic();

  // `soonest_available_time` is null when there is no availability.
  if (!info.soonest_available_time || !isValidDate(info.soonest_available_time)) {
    // TODO - UI for no availability
    return <AppointmentBookingPage.Error onPrevious={onPrevious} />;
  }

  // TODO - remove this once we remove the USE_CLINIC_TIMEZONE feature flag
  const nextAvailableDate = window.CLINIC_TIMEZONE
    ? fromISODateTime(info.soonest_available_time)
    : fromISODateTimeInTimezone(info.soonest_available_time, clinic.timezone);

  return (
    <AppointmentBookingPage
      step={AppointmentBookingStep.Start}
      onPrevious={onPrevious}
      content={
        <div className={styles.start}>
          {/* While we redirect to start for any "invalid appt id" state, 99% of the time it will be due
          to the appt having been deleted due to inactivity (10 min) */}
          {search.is_expired && (
            <Alert className={styles.alert} variant="warning">
              Your in-progress booking has been canceled due to inactivity, please view updated
              appointment availability.
            </Alert>
          )}
          <div className={styles.title}>Let’s schedule your next appointment!</div>
          <Image src={dogImage} alt="Welcome dog" className={styles.image} />
          <div className={styles.availability}>
            <Image src={gradient} alt="Gradient" className={styles.gradient} />
            <div className={styles.soonest}>
              Soonest availability:{" "}
              <span className={styles.highlight}>{getNextAvailableLabel(nextAvailableDate)}</span>
            </div>
            <div className={styles.date}>
              {toLocaleDate(nextAvailableDate, { weekday: "long" })} ·{" "}
              {toLocaleDate(nextAvailableDate, {
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>
          <div className={styles.warning}>
            If your pet is sick or injured, and requires immediate attention, please call us
            directly
            {clinic.phone_number ? (
              <>
                {" "}
                at{" "}
                <a href={`tel:${clinic.phone_number}`} className={styles.phone}>
                  {toPhone(clinic.phone_number)}
                </a>
              </>
            ) : (
              ""
            )}
            .
          </div>
        </div>
      }
      button={
        <Button onClick={onNext}>
          Book an appointment <Icon icon={faArrowRight} />
        </Button>
      }
    />
  );
};

const AppointmentBookingPets = ({
  onPrevious,
  onNext,
  info,
  appointments,
  client,
}: AppointmentBookingStepProps) => {
  const navigate = useNavigate();
  const { open: openAlert } = useAppointmentBookingAlertDrawer();

  const calendar = useDefaultCalendarId();

  const [patients, setPatients] = useState<PatientForMultiSelectPanel[]>(
    appointments.map((a) => a.patient),
  );

  const {
    mutate: createAppointments,
    isPending: isLoading,
    isSuccess,
    error,
  } = useAppointmentBulkCreateMutation({
    onSuccess: (created) => {
      navigate(
        urls.scheduler.book({
          params: { step: AppointmentBookingStep.Reason },
          search: { appointment_ids: created.map((a) => a.id).join(",") },
        }),
      );
    },
  });

  const handleCreate = () => {
    createAppointments({ calendar, patients: patients.map((p) => p.id) });
  };

  const handleNext = () => {
    const selectedPatientIds = new Set(patients.map((p) => p.id));
    const apptPatientIds = new Set(appointments.map((a) => a.patient.id));
    const blockedPatient = patients.find((p) => !info.allowed_species.includes(p.species));

    // Check if species is available for booking at this clinic
    if (info.allowed_species.length > 0 && blockedPatient) {
      openAlert({
        isEmergency: false,
        appointmentKind: null,
        blockedPatient,
      });
      return;
    }

    if (!appointments.length || !equals(selectedPatientIds, apptPatientIds)) {
      handleCreate();
    } else {
      onNext();
    }
  };

  if (!client) {
    return <AppointmentBookingPage.Error onPrevious={onPrevious} />;
  }

  return (
    <AppointmentBookingPage
      step={AppointmentBookingStep.Pets}
      onPrevious={onPrevious}
      error={error?.detail}
      content={
        <>
          <div className={styles.title}>Select which pet(s) this appointment is for</div>
          <PatientMultiSelect values={patients} onChange={setPatients} />
        </>
      }
      button={
        <Button onClick={handleNext} isLoading={isLoading || isSuccess} disabled={!patients.length}>
          Continue <Icon icon={faArrowRight} />
        </Button>
      }
    />
  );
};

const AppointmentBookingReason = ({
  onPrevious,
  onNext,
  info,
  appointments,
}: AppointmentBookingStepProps) => {
  const calendar = useDefaultCalendarId();

  const { open: openAlert } = useAppointmentBookingAlertDrawer();

  const [apptIndex, setApptIndex] = useState(0);
  const activeAppt = appointments[apptIndex];
  const isFirstAppt = apptIndex === 0;
  const isLastAppt = apptIndex === appointments.length - 1;
  // Let turbo classify on all appointment kind options, not just enabled ones
  const nonExoticKinds = Object.values(AppointmentKindSlug).filter(
    (kind) => !Object.values(appointmentKindToExoticKind).includes(kind),
  );

  const [reason, setReason] = useState(activeAppt.reason);
  useEffect(() => {
    setReason(activeAppt.reason);
    // Reset the reason when we switch between appts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apptIndex]);

  const {
    mutate: updateAppointments,
    isPending: isLoading,
    error,
  } = useAppointmentBulkUpdateMutation({
    onSuccess: () => {
      if (isLastAppt) {
        onNext();
      } else {
        setApptIndex((i) => i + 1);
      }
    },
  });

  const [classification, setClassification] = useState<ClassifyAppointmentKindResult | null>(null);

  const {
    mutate: classifyAppt,
    isPending: isClassifying,
    error: classifyError,
  } = useClassifyApptMutation({
    onSuccess: setClassification,
  });
  const handleClassifyAppt = useDebounceCallback(
    (r: string) =>
      r &&
      // turbo expects only non-exotic kinds
      classifyAppt({
        available_slugs: nonExoticKinds,
        reason: r,
      }),
  );

  const [hasClickedNext, setHasClickedNext] = useState(false);

  const { appointmentKind, appointmentKindBookable, blockedPatient, isEmergency } =
    getAppointmentBookingInfo(classification, activeAppt.patient, info);

  const handleNext = () => {
    // If we're still waiting on the classify request, set a flag to submit once we have the data
    if (!hasClickedNext && isClassifying) {
      setHasClickedNext(true);
      return;
    }

    // Skip classification locally since most wont have turbo running
    if (isDev) {
      const bookableKind =
        info.enabled_appointment_kinds.find((kind) => kind.is_client_bookable) ||
        info.enabled_appointment_kinds[0];
      updateAppointments({
        appointment_ids: [activeAppt.id],
        reason,
        calendar_id: calendar,
        kind: bookableKind.id,
      });
      return;
    }

    if (classifyError || !classification) {
      openAlert({ isEmergency: false, appointmentKind: null });
      return;
    }

    if (!appointmentKind || !appointmentKindBookable || isEmergency) {
      openAlert({
        isEmergency,
        appointmentKind,
        blockedPatient,
      });
      return;
    }

    if (reason !== activeAppt.reason) {
      updateAppointments({
        appointment_ids: [activeAppt.id],
        reason,
        calendar_id: calendar,
        kind: appointmentKind.id,
      });
    } else {
      onNext();
    }
  };
  const handleReasonChange = (r: string) => {
    setReason(r);
  };

  const handlePrevious = () => {
    if (isFirstAppt) {
      onPrevious();
    } else {
      setApptIndex((i) => i - 1);
    }
  };

  // Make sure classification updated when reason changes and on initial load
  useEffect(() => {
    handleClassifyAppt(reason);
  }, [reason, handleClassifyAppt]);

  useEffect(() => {
    if (hasClickedNext && (classification || classifyError)) {
      handleNext();
      setHasClickedNext(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classification, classifyError]);

  return (
    <AppointmentBookingPage
      step={AppointmentBookingStep.Reason}
      onPrevious={handlePrevious}
      content={
        <div className={styles.reason}>
          <div className={styles.title}>
            What is the reason for {activeAppt.patient.name_display}&apos;s visit?
          </div>
          {error?.errors.reason && <Alert variant="danger">{error.errors.reason}</Alert>}
          <textarea
            value={reason}
            onChange={(event) => handleReasonChange(event.target.value)}
            className={styles.textarea}
            placeholder="Enter reason for visit..."
          />
        </div>
      }
      button={
        <Button onClick={handleNext} disabled={!reason} isLoading={isLoading || hasClickedNext}>
          {isLastAppt ? `View appointment schedule` : "Continue to next pet"}{" "}
          <Icon icon={faArrowRight} />
        </Button>
      }
    />
  );
};

const getToday = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
};

const getScrollableDays = (date: Date) => {
  const today = getToday();
  const currMonth = new Date(date.getTime());
  currMonth.setDate(1);

  const currDate = new Date(currMonth.getTime());
  const dates: Date[] = [];
  while (currDate.getMonth() === currMonth.getMonth()) {
    if (currDate >= today) {
      dates.push(new Date(currDate.getTime()));
    }
    currDate.setDate(currDate.getDate() + 1);
  }
  return dates;
};

type DayPart = "morning" | "afternoon";

const getTimesByDayPart = (times: ResourceAvailabilityByTime[], clinic: Clinic) => {
  const timesByDayPart: Record<DayPart, ResourceAvailabilityByTime[]> = {
    morning: [],
    afternoon: [],
  };

  const now = new Date();

  times.forEach((time) => {
    // TODO - remove this once we remove the USE_CLINIC_TIMEZONE feature flag
    const thisTime = window.CLINIC_TIMEZONE
      ? toClinicDate(time.start_datetime)
      : fromISODateTimeInTimezone(time.start_datetime, clinic.timezone);

    if (thisTime < now) {
      return;
    }

    const hour = thisTime.getHours();
    if (hour < 12) {
      timesByDayPart.morning.push(time);
    } else {
      timesByDayPart.afternoon.push(time);
    }
  });

  return timesByDayPart;
};

const isSameApptTime = (a: { start_datetime: string }, b: { start_datetime: string }) =>
  isSameDateTime(fromISODateTime(a.start_datetime), fromISODateTime(b.start_datetime));

const AppointmentBookingDateTime = ({
  onPrevious,
  onNext,
  appointments,
  info,
}: AppointmentBookingStepProps) => {
  const queryClient = useQueryClient();
  const calendar = useDefaultCalendarId();
  const clinic = useClinic();

  const today = getToday();

  // The date selected in the UI for viewing times
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  // The first date in the UI list that we have scrolled to
  const [scrollDate, setScrollDate] = useState<Date>(today);
  const scrollableDays = getScrollableDays(scrollDate);
  const dayRefs = useRef<(LinkButtonRef | null)[]>([]);
  useLayoutEffect(() => {
    const index = scrollableDays.findIndex((d) => isSameDate(d, scrollDate));
    dayRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "start",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDate]);

  const [isByTimeSlot, setIsByTimeSlot] = useState(true);

  const [activeApptIndex, setActiveApptIndex] = useState(0);
  const activeAppt: ClientAppointment | undefined = appointments[activeApptIndex];

  const [isExpanded, setIsExpanded] = useState(false);

  const {
    data: byTime,
    isLoading: isTimeLoading,
    error: byTimeError,
  } = useCalendarResourceAvailabilityByTimeQuery(calendar, {
    date: toISODate(selectedDate),
    appointment_kind: activeAppt.kind?.id,
  });

  const {
    data: byResource,
    isLoading: isResourceLoading,
    error: byResourceError,
  } = useCalendarResourceAvailabilityByResourceQuery(calendar, {
    date: toISODate(selectedDate),
    appointment_kind: activeAppt.kind?.id,
  });

  // Invalidate queries after some time in case the user leaves the page open
  useEffect(() => {
    const timeout = setInterval(
      () => {
        queryClient.invalidateQueries({
          queryKey: keys.resourceAvailabilities(),
        });
        queryClient.invalidateQueries({ queryKey: keys.timeAvailabilities() });
      },
      1000 * 60 * 5,
    ); // reset after 5 mins
    return () => clearInterval(timeout);
  });

  const byResourceSorted = byResource?.sort((a, b) => {
    if (a.availability_datetimes.length === 0) {
      return 1;
    }
    if (b.availability_datetimes.length === 0) {
      return -1;
    }

    return a.resource.name.localeCompare(b.resource.name);
  });

  const { mutate: update, error: updateError } = useAppointmentBulkUpdateOptimisticMutation();

  const handleScrollDate = (delta: 1 | -1) => {
    const dayDelta = delta * 6; // UI is 6 days wide;
    let newDate = new Date(scrollDate.getTime());
    newDate.setDate(scrollDate.getDate() + dayDelta);
    // When changing months, make sure we scroll to the start/end accordingly
    if (newDate.getMonth() !== scrollDate.getMonth()) {
      if (delta === 1) {
        newDate.setDate(1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1); // set to the next month
        newDate.setDate(0); // 0th day will be the last day of the previous month
      }
    }
    if (newDate < today) {
      newDate = new Date(today.getTime());
    }
    setScrollDate(newDate);
  };

  const handleNext = () => {
    if (activeApptIndex === appointments.length - 1) {
      onNext();
    } else {
      setActiveApptIndex((i) => i + 1);
    }
  };

  const seeSoonestAvailability = () => {
    // `soonest_available_time` is null when there is no availability.
    if (!info.soonest_available_time || !isValidDate(info.soonest_available_time)) {
      return;
    }
    const date = window.CLINIC_TIMEZONE
      ? fromISODateTime(info.soonest_available_time)
      : fromISODateTimeInTimezone(info.soonest_available_time, clinic.timezone);
    setSelectedDate(date);
    setScrollDate(date);
  };

  const renderResourceTimes = (resourceData: ResourceAvailabilityByResource) => {
    if (!canProviderSeeSpecies(resourceData.resource, activeAppt.patient.species)) {
      return (
        <Alert variant="info" className={styles.timeSlotWarning}>
          Provider not available for your pet&apos;s species
        </Alert>
      );
    }
    if (resourceData.availability_datetimes.length === 0) {
      return (
        <Alert variant="info" className={styles.timeSlotWarning}>
          No appointments available on this day
        </Alert>
      );
    }

    return (
      <div className={styles.timeSlots}>
        {resourceData.availability_datetimes.map((time) => {
          // TODO - remove this once we remove the USE_CLINIC_TIMEZONE feature flag
          const start = window.CLINIC_TIMEZONE
            ? fromISODateTime(time.start_datetime)
            : fromISODateTimeInTimezone(time.start_datetime, clinic.timezone);
          if (start < new Date()) {
            return null;
          }

          return (
            <SelectButton
              key={time.start_datetime}
              isActive={
                isSameApptTime(time, activeAppt) &&
                activeAppt.resource?.id === resourceData.resource.id
              }
              disabled={appointments.some(
                (appt) =>
                  appt.id !== activeAppt.id &&
                  isSameApptTime(time, appt) &&
                  appt.resource?.id === resourceData.resource.id,
              )}
              align="center"
              onClick={() =>
                update({
                  body: {
                    start_datetime: time.start_datetime,
                    end_datetime: time.end_datetime,
                    resource: resourceData.resource.id,
                    appointment_ids: [activeAppt.id],
                    calendar_id: calendar,
                  },
                  entities: { resource: resourceData.resource },
                })
              }
              buttonClassName={styles.timeSlotButton}
              renderContent={() => <div className={styles.timeSlot}>{toTime(start)}</div>}
            />
          );
        })}
      </div>
    );
  };

  const renderTimes = () => {
    if (isByTimeSlot) {
      if (isTimeLoading) {
        return <LoadingContainer />;
      }

      if (byTimeError) {
        return <FetchErrorContainer error={byTimeError} entityName="times" isPlural={true} />;
      }

      if (!byTime?.length) {
        return (
          <>
            <Alert variant="info">No appointments available on this day</Alert>{" "}
            <div className={styles.soonestAvailability}>
              <PillButton variant="secondary" size="xs" onClick={seeSoonestAvailability}>
                See next available appointment
              </PillButton>
            </div>
          </>
        );
      }

      const dayPartToTimes: Record<DayPart, string> = {
        morning: "8:00 - 12:00",
        afternoon: "12:00 - 5:00",
      };

      return Object.entries(getTimesByDayPart(byTime, clinic)).map(([dayPart, times]) => {
        const filteredTimes = times
          .map((time) => {
            return {
              ...time,
              resources: time.resources.filter((resource) =>
                canProviderSeeSpecies(resource, activeAppt.patient.species),
              ),
            };
          })
          .filter((time) => time.resources.length > 0);

        return (
          <div key={dayPart}>
            <span className={styles.dayPartTitle}>
              {titleCased(dayPart)} ·{" "}
              <span className={styles.dayPartTimes}>{dayPartToTimes[dayPart as DayPart]}</span>
            </span>
            {filteredTimes.length === 0 && (
              <Alert variant="info" className={styles.timeSlotWarning}>
                No appointments available
              </Alert>
            )}
            {filteredTimes.length > 0 && (
              <motion.div className={styles.timeSlots}>
                {filteredTimes.map((time) => (
                  <SelectButton
                    key={time.start_datetime}
                    isActive={isSameApptTime(time, activeAppt)}
                    disabled={appointments.some(
                      (appt) => appt.id !== activeAppt.id && isSameApptTime(time, appt),
                    )}
                    align="center"
                    onClick={() =>
                      update({
                        body: {
                          start_datetime: time.start_datetime,
                          end_datetime: time.end_datetime,
                          // TODO - resource select in time screen?
                          resource: time.resources[0].id,
                          appointment_ids: [activeAppt.id],
                          calendar_id: calendar,
                        },
                        entities: { resource: time.resources[0] },
                      })
                    }
                    buttonClassName={styles.timeSlotButton}
                    renderContent={() => (
                      <div className={styles.timeSlot}>
                        {/* TODO - remove this once we remove the USE_CLINIC_TIMEZONE feature flag */}
                        {window.CLINIC_TIMEZONE
                          ? toTime(time.start_datetime)
                          : toTime(fromISODateTimeInTimezone(time.start_datetime, clinic.timezone))}
                      </div>
                    )}
                  />
                ))}
              </motion.div>
            )}
          </div>
        );
      });
    }

    // else, by resource
    if (isResourceLoading) {
      return <LoadingContainer />;
    }

    if (byResourceError) {
      return <FetchErrorContainer error={byResourceError} entityName="doctors" isPlural={true} />;
    }

    if (!byResourceSorted?.length) {
      return (
        <>
          <Alert variant="info">No appointments available on this day</Alert>{" "}
          <div className={styles.soonestAvailability}>
            <PillButton variant="secondary" size="xs" onClick={seeSoonestAvailability}>
              See next available appointment
            </PillButton>
          </div>
        </>
      );
    }

    return byResourceSorted.map((resourceData) => (
      <div
        key={resourceData.resource.id}
        className={cx(resourceData.availability_datetimes.length === 0 && styles.noApptDoctor)}
      >
        <div className={styles.doctorName}>
          <ResourceImage resource={resourceData.resource} className={styles.image} />
          <div>{resourceData.resource.name}</div>
        </div>
        {renderResourceTimes(resourceData)}
      </div>
    ));
  };

  // Usually we show this button by the "no appointments available" message,
  // but when viewing by resource, with valid resources but no times available, we show it above the times
  // so that we don't show it multiple times
  const showSeeNextAvailableButtonAboveTimes =
    !isByTimeSlot &&
    byResourceSorted?.length &&
    byResourceSorted.every(
      (resourceData) =>
        !canProviderSeeSpecies(resourceData.resource, activeAppt.patient.species) ||
        resourceData.availability_datetimes.length === 0,
    );

  const renderAppt = (appt: ClientAppointment) => {
    let start = appt.start_datetime
      ? fromISODateTimeInTimezone(appt.start_datetime, clinic.timezone)
      : null;
    // TODO - remove this once we remove the USE_CLINIC_TIMEZONE feature flag
    if (appt.start_datetime && window.CLINIC_TIMEZONE) {
      start = fromISODateTime(appt.start_datetime);
    }

    const apptInfo = [
      ...(start ? [toLocaleDate(start, { month: "short", day: "numeric" })] : []),
      ...(start ? [toTime(start)] : []),
      appt.resource?.name,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <div>
        <span className={styles.apptTitle}>
          <strong>{appt.patient.name_display}:</strong> {appt.kind?.label ?? "Appointment"}
        </span>
        {start ? (
          <div className={styles.apptTime}>{apptInfo}</div>
        ) : (
          <div className={styles.selectATime}>Select a time</div>
        )}
      </div>
    );
  };

  const controls = useDragControls();
  const y = useMotionValue(0);

  const handleDrag = () => {
    const delta = y.get();
    if (delta > 25 && isExpanded) {
      setIsExpanded(false);
    }
    if (delta < -25 && !isExpanded) {
      setIsExpanded(true);
    }
  };

  // Prevent drag from triggering clicks
  const mouseDownRef = useRef({ x: 0, y: 0 });
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownRef.current = { x: e.pageX, y: e.pageY };
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    const diffX = Math.abs(e.pageX - mouseDownRef.current.x);
    const diffY = Math.abs(e.pageY - mouseDownRef.current.y);
    if (diffX < 10 && diffY < 10) {
      setIsExpanded(!isExpanded);
    }
  };

  const userTimezone = getTimezone();

  return (
    <AppointmentBookingPage
      step={AppointmentBookingStep.DateTime}
      onPrevious={onPrevious}
      content={
        <div className={styles.datetime}>
          <div className={styles.title}>
            {toLocaleDate(scrollDate, { month: "long", year: "numeric" })}
            <div className={styles.nav}>
              <Button
                onClick={() => handleScrollDate(-1)}
                disabled={isSameDate(scrollDate, today)}
                variant="secondary"
                size="xs"
              >
                <Icon icon={faArrowLeft} />
              </Button>
              <Button onClick={() => handleScrollDate(1)} variant="secondary" size="xs">
                <Icon icon={faArrowRight} />
              </Button>
            </div>
          </div>
          <div className={styles.days}>
            {scrollableDays.map((d, i) => (
              <Button
                key={d.getTime()}
                onClick={() => setSelectedDate(d)}
                className={cx(styles.day, isSameDate(d, selectedDate) && styles.active)}
                variant="secondary"
                ref={(el) => {
                  dayRefs.current[i] = el;
                }}
              >
                <div className={styles.dayInner}>
                  <div className={styles.weekday}>{toLocaleDate(d, { weekday: "short" })}</div>
                  <div className={styles.dayNumber}>{toLocaleDate(d, { day: "numeric" })}</div>
                </div>
              </Button>
            ))}
          </div>

          <div className={styles.buttons}>
            <SelectButton
              isActive={isByTimeSlot}
              onClick={() => setIsByTimeSlot(true)}
              renderContent={() => "View by time slot"}
              className={styles.leftButton}
              buttonClassName={styles.selectButton}
            />
            <SelectButton
              isActive={!isByTimeSlot}
              onClick={() => setIsByTimeSlot(false)}
              renderContent={() => "View by doctor"}
              className={styles.rightButton}
              buttonClassName={styles.selectButton}
            />
          </div>
          <div className={styles.times}>
            {userTimezone && !hasSameTimezoneOffset(userTimezone, clinic.timezone) && (
              <Alert variant="warning">
                You are in a different timezone ({userTimezone.replace("_", " ")}) than the clinic (
                {clinic.timezone.replace("_", " ")}). The times below are displayed in the
                clinic&apos;s timezone.
              </Alert>
            )}
            {updateError && (
              <Alert variant="danger">
                {updateError.errors.start_datetime ?? updateError.detail}
              </Alert>
            )}
            {showSeeNextAvailableButtonAboveTimes && (
              <div className={styles.soonestAvailability}>
                <PillButton variant="secondary" size="xs" onClick={seeSoonestAvailability}>
                  See next available appointment
                </PillButton>
              </div>
            )}
            {renderTimes()}
          </div>
          <motion.div
            drag={appointments.length > 1 ? "y" : false}
            animate="y"
            // Without the x value, motion will override the css transform we use to center this
            // y value is used to monitor height
            style={{ x: "-50%", y }}
            // Restrict drag control to the button
            dragControls={controls}
            dragListener={false}
            // Restrict the draggable element from moving away from it's starting position
            dragConstraints={{ top: 0, bottom: 0 }}
            // Allow a bit of drag in only the open/close direction, when applicable
            dragElastic={isExpanded ? { top: 0, bottom: 0.2 } : { top: 0.2, bottom: 0 }}
            onDrag={handleDrag}
            // Prevent momentum from a downward drag sending it off screen even though our props don't allow downward movement at all 🤯
            dragMomentum={false}
            className={styles.overlay}
          >
            {appointments.length > 1 && (
              <button
                type="button"
                onPointerDown={(e) => controls.start(e)}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                className={styles.dragButton}
              >
                <div className={styles.drag} />
                <span className={styles.visuallyHidden}>Drag to reorder</span>
              </button>
            )}
            {isExpanded ? (
              <div className={styles.expanded}>
                <Alert variant="info" className={styles.alert}>
                  <strong>Need to make a change?</strong> Simply click on a row below to edit that
                  pet’s appointment selection.
                </Alert>
                {appointments.map((appt, i) => (
                  <button
                    type="button"
                    className={styles.apptRow}
                    key={appt.id}
                    onClick={() => setActiveApptIndex(i)}
                  >
                    {renderAppt(appt)}
                    {i === activeApptIndex && <Icon icon={faCheck} />}
                  </button>
                ))}
                <Button
                  onClick={onNext}
                  disabled={!appointments.every((appt) => appt.start_datetime && appt.resource)}
                >
                  Looks good, continue
                  <Icon icon={faArrowRight} />
                </Button>
              </div>
            ) : (
              <div className={styles.collapsed}>
                {renderAppt(activeAppt)}
                <Button
                  onClick={handleNext}
                  disabled={!activeAppt.start_datetime || !activeAppt.resource}
                  className={styles.nextButton}
                  size="xs"
                >
                  <Icon icon={faArrowRight} />
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      }
    />
  );
};

const AppointmentBookingReview = ({
  onPrevious,
  onNext,
  appointments,
  client,
}: AppointmentBookingStepProps) => {
  const calendar = useDefaultCalendarId();
  const appointmentCount = appointments.length;

  const { open: openAccountUpdate } = useAccountUpdateDrawer();
  const { open: openCreatePayment } = useAccountCreatePaymentDrawer();
  const {
    mutate: updateAppts,
    isPending: isApptLoading,
    isSuccess: isApptSuccess,
    error: apptError,
  } = useAppointmentBulkUpdateMutation({ onSuccess: onNext });
  const handleUpdate = () => {
    updateAppts({
      appointment_ids: appointments.map((a) => a.id),
      status: AppointmentStatus.ClientRequested,
      calendar_id: calendar,
    });
  };

  const {
    mutate: updateClient,
    isPending: isClientLoading,
    isSuccess: isClientSuccess,
    error: clientError,
  } = useUpdateClientMutation({ onSuccess: handleUpdate });

  const [paymentMethod, setPaymentMethod] = useState(() => {
    const primary = client?.primary_payment_method;
    // Don't pre-select an expired primary card — it can't be used to book.
    return primary && isPaymentOptionChargeable(primary) ? primary : undefined;
  });

  if (!client) {
    return <AppointmentBookingPage.Error onPrevious={onPrevious} />;
  }

  // Expired/removed cards stay in the list but sink below chargeable ones. A stable
  // sort keeps each group in its original order.
  const sortedPaymentMethods = [...client.payment_methods].sort(
    (a, b) => Number(isPaymentOptionChargeable(b)) - Number(isPaymentOptionChargeable(a)),
  );

  const contactInfo = [
    { label: "Name", value: client.primary_client_user.full_name },
    { label: "Phone", value: client.primary_contact_number?.number_display },
    { label: "Email", value: client.primary_contact_email?.email },
  ];

  const handleNext = () => {
    // If client made a payment method selection, set it as their primary before proceeding
    if (paymentMethod?.id !== client?.primary_payment_method?.id) {
      updateClient({ primary_payment_method: paymentMethod?.id });
    } else {
      handleUpdate();
    }
  };

  return (
    <AppointmentBookingPage
      step={AppointmentBookingStep.Review}
      onPrevious={onPrevious}
      content={
        <div className={styles.review}>
          <div className={styles.appointments}>
            {appointments.map((appt) => (
              <AppointmentBookingAppointment appoinment={appt} key={appt.id} />
            ))}
          </div>
          <div className={styles.contact}>
            <div className={styles.title}>
              Contact information
              <PillButton
                variant="secondary"
                size="xs"
                onClick={() => openAccountUpdate({ updatePhoneNumber: true })}
              >
                <Icon icon={faPencil} className={styles.editIcon} />
                Edit
              </PillButton>
            </div>
            <div className={styles.box}>
              {contactInfo.map((info) => (
                <div className={styles.infoRow} key={info.label}>
                  <div className={styles.label}>{info.label}</div>
                  <div className={styles.value}>{info.value}</div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.payment}>
            <div className={styles.title}>Payment information</div>
            <div className={styles.subtitle}>
              Select a preferred payment method to your account to make checkout easy.{" "}
              <strong>You will not be charged now.</strong> <i>(optional)</i>
            </div>
            <div className={styles.methods}>
              {clientError && (
                <Alert variant="danger">
                  {clientError.errors.primary_payment_method ?? clientError.detail}
                </Alert>
              )}
              {sortedPaymentMethods.map((method) => {
                const chargeabilityChipLabel = getPaymentOptionChargeabilityChip(method)?.label;
                return (
                  <SelectButton
                    key={method.id}
                    isActive={method.id === paymentMethod?.id}
                    disabled={!isPaymentOptionChargeable(method)}
                    onClick={() => setPaymentMethod(method)}
                    renderContent={({ isActive }) => (
                      <div className={styles.method}>
                        <PaymentMethod
                          method={method}
                          styles={{
                            label: styles.methodLabel,
                            description: styles.methodDescription,
                            image: styles.methodImage,
                          }}
                        />
                        {chargeabilityChipLabel && (
                          <TextChip variant="danger">{chargeabilityChipLabel}</TextChip>
                        )}
                        {isActive && <Icon icon={faCircleCheck} />}
                      </div>
                    )}
                  />
                );
              })}
              <SelectButton
                isActive={false}
                onClick={() => openCreatePayment({})}
                renderContent={() => (
                  <div className={styles.method}>
                    <div className={styles.addMethod}>
                      <Image src={blankPayment} alt="Blank card" className={styles.methodImage} />
                      <div className={styles.addMethodLabel}>
                        <span>Add payment method</span>
                        <span className={styles.addMethodDescription}>Enter information</span>
                      </div>
                    </div>
                    <Icon icon={faPlus} />
                  </div>
                )}
              />
            </div>
          </div>
          {apptError?.errors.status && <Alert variant="danger">{apptError.errors.status}</Alert>}
        </div>
      }
      button={
        <Button
          onClick={handleNext}
          isLoading={isClientLoading || isClientSuccess || isApptLoading || isApptSuccess}
        >
          Confirm {appointmentCount > 1 ? toCount(appointmentCount, "appointment") : "appointment"}
        </Button>
      }
    />
  );
};

export default {
  Start: AppointmentBookingStart,
  Pets: AppointmentBookingPets,
  Reason: AppointmentBookingReason,
  DateTime: AppointmentBookingDateTime,
  Review: AppointmentBookingReview,
};
